/**
 * Permissions du contrat : rôles, matrice « Qui voit quoi » (presentation/lakou-successions.html, §04)
 * et catalogue des permissions `ressource:action:portée` déclarées par les routes.
 * Le moteur qui les applique est `packages/domain/policy` (L14) ; ce fichier est la vérité testée.
 */

export const ROLES = [
  { code: 'admin', libelle: 'Administration', equipe: true },
  { code: 'dirigeante', libelle: 'Dirigeante (Isabelle)', equipe: true },
  { code: 'assistante', libelle: 'Assistante (Brigitte)', equipe: true },
  { code: 'agent', libelle: 'Agent MAAT', equipe: true },
  { code: 'clerc', libelle: 'Clerc de notaire', equipe: false },
  { code: 'notaire', libelle: 'Notaire', equipe: false },
  { code: 'mandant', libelle: 'Mandant', equipe: false },
  { code: 'heritier', libelle: 'Héritier', equipe: false },
  { code: 'prestataire', libelle: 'Prestataire', equipe: false },
  { code: 'representant_legal', libelle: 'Représentant légal', equipe: false },
  { code: 'contact_delegue', libelle: 'Contact délégué', equipe: false },
] as const;

export type RoleCode = (typeof ROLES)[number]['code'];
export const CODES_ROLES = ROLES.map((r) => r.code) as unknown as readonly [RoleCode, ...RoleCode[]];

/** Colonnes du tableau de la présentation → rôles du contrat. */
export const COLONNES_PRESENTATION: Readonly<Record<string, RoleCode>> = {
  Isabelle: 'dirigeante',
  Brigitte: 'assistante',
  Agent: 'agent',
  Clerc: 'clerc',
  Mandant: 'mandant',
  Héritier: 'heritier',
  Prestataire: 'prestataire',
};

export const VALEURS_DROIT = [
  { code: 'oui', libelle: 'oui', description: 'Accès complet.' },
  { code: 'non', libelle: 'non', description: 'Aucun accès.' },
  { code: 'sans_montants', libelle: 'sans €', description: 'Pilotage sans aucun montant.' },
  { code: 'sa_branche', libelle: 'sa branche', description: 'Seulement la branche de la personne.' },
  { code: 'si_besoin', libelle: 'si besoin', description: 'Sur demande journalisée, pour les actes.' },
  { code: 'non_masquees', libelle: 'non masquées', description: 'Seulement les coordonnées que la personne a accepté de partager.' },
  { code: 'valide', libelle: 'validé', description: 'Préparé, puis validé par Isabelle avant le départ.' },
  { code: 'sa_mission', libelle: 'sa mission', description: 'Seulement le périmètre de la mission confiée.' },
  { code: 'detaillee', libelle: 'détaillée', description: 'Progression complète, étapes et blocages.' },
  { code: 'notariale', libelle: 'notariale', description: 'Progression utile aux actes notariés.' },
  { code: 'empreinte', libelle: 'empreinte', description: 'Empreinte de progression simplifiée (étapes client).' },
  { code: 'pieces', libelle: 'pièces', description: 'Journal limité aux pièces.' },
  { code: 'ses_echanges', libelle: 'ses échanges', description: 'Journal limité à ses propres échanges.' },
] as const;

export type ValeurDroit = (typeof VALEURS_DROIT)[number]['code'];

export const libelleValeur = (valeur: ValeurDroit): string => {
  const v = VALEURS_DROIT.find((x) => x.code === valeur);
  if (!v) throw new Error(`Valeur de droit inconnue : ${valeur}`);
  return v.libelle;
};

export const LIGNES_MATRICE = [
  { code: 'pilotage_financier', libelle: 'Tous les dossiers et le pilotage financier' },
  { code: 'arbre_complet', libelle: 'Arbre complet de la famille' },
  { code: 'coordonnees_heritiers', libelle: 'Coordonnées des héritiers' },
  { code: 'notes_internes', libelle: 'Notes internes (pistes, Facebook, ressenti)' },
  { code: 'relances', libelle: 'Déclencher ou suspendre une relance' },
  { code: 'whatsapp_isabelle', libelle: "Écrire depuis le WhatsApp d'Isabelle" },
  { code: 'deposer_piece', libelle: "Déposer une pièce (photo d'acte)" },
  { code: 'progression', libelle: 'Progression du dossier' },
  { code: 'journal_exportable', libelle: 'Journal horodaté exportable' },
] as const;

export type LigneMatrice = (typeof LIGNES_MATRICE)[number]['code'];

export interface Cellule {
  valeur: ValeurDroit;
  /** `presentation` : recopié du tableau ; `propose_a_valider` : rôle absent du tableau, proposition à faire valider. */
  source: 'presentation' | 'propose_a_valider';
}

const p = (valeur: ValeurDroit): Cellule => ({ valeur, source: 'presentation' });
const a = (valeur: ValeurDroit): Cellule => ({ valeur, source: 'propose_a_valider' });

/**
 * Colonnes ordonnées : admin, dirigeante, assistante, agent, clerc, notaire, mandant, heritier, prestataire,
 * representant_legal, contact_delegue. L'administratrice est Isabelle : mêmes droits que la dirigeante.
 * Notaire, représentant légal et contact délégué ne figurent pas dans la présentation (ADR 0001 du paquet).
 */
const ligne = (
  admin: Cellule,
  dirigeante: Cellule,
  assistante: Cellule,
  agent: Cellule,
  clerc: Cellule,
  notaire: Cellule,
  mandant: Cellule,
  heritier: Cellule,
  prestataire: Cellule,
  representant_legal: Cellule,
  contact_delegue: Cellule,
): Record<RoleCode, Cellule> => ({
  admin,
  dirigeante,
  assistante,
  agent,
  clerc,
  notaire,
  mandant,
  heritier,
  prestataire,
  representant_legal,
  contact_delegue,
});

export const MATRICE: Readonly<Record<LigneMatrice, Readonly<Record<RoleCode, Cellule>>>> = {
  // prettier-ignore
  pilotage_financier: ligne(a('oui'), p('oui'), p('sans_montants'), p('non'), p('non'), a('non'), p('non'), p('non'), p('non'), a('non'), a('non')),
  // prettier-ignore
  arbre_complet: ligne(a('oui'), p('oui'), p('oui'), p('oui'), p('oui'), a('oui'), p('sa_branche'), p('sa_branche'), p('non'), a('sa_branche'), a('non')),
  // prettier-ignore
  coordonnees_heritiers: ligne(a('oui'), p('oui'), p('oui'), p('oui'), p('si_besoin'), a('si_besoin'), p('non_masquees'), p('non_masquees'), p('non'), a('non_masquees'), a('non')),
  // prettier-ignore
  notes_internes: ligne(a('oui'), p('oui'), p('oui'), p('oui'), p('non'), a('non'), p('non'), p('non'), p('non'), a('non'), a('non')),
  // prettier-ignore
  relances: ligne(a('oui'), p('oui'), p('oui'), p('oui'), p('non'), a('non'), p('non'), p('non'), p('non'), a('non'), a('non')),
  // prettier-ignore
  whatsapp_isabelle: ligne(a('oui'), p('oui'), p('valide'), p('non'), p('non'), a('non'), p('non'), p('non'), p('non'), a('non'), a('non')),
  // prettier-ignore
  // Notaire : « non » (alerte 3 du 26/10, ARB-NOTAIRE-NF01) : son espace ne téléverse rien ; la clerc dépose. Toujours à valider.
  deposer_piece: ligne(a('oui'), p('oui'), p('oui'), p('oui'), p('oui'), a('non'), p('oui'), p('oui'), p('sa_mission'), a('oui'), a('oui')),
  // prettier-ignore
  progression: ligne(a('detaillee'), p('detaillee'), p('detaillee'), p('detaillee'), p('notariale'), a('notariale'), p('empreinte'), p('empreinte'), p('sa_mission'), a('empreinte'), a('empreinte')),
  // prettier-ignore
  journal_exportable: ligne(a('oui'), p('oui'), p('oui'), p('non'), p('pieces'), a('pieces'), p('ses_echanges'), p('ses_echanges'), p('non'), a('ses_echanges'), a('ses_echanges')),
};

export const droit = (ligneCode: LigneMatrice, role: RoleCode): Cellule => MATRICE[ligneCode][role];

/** Permission déclarée par une route : `ressource:action:portée`. */
export interface Permission {
  code: string;
  description: string;
  /** Ligne de la matrice « Qui voit quoi » que la permission met en œuvre, si elle en dépend. */
  ligne?: LigneMatrice;
}

export const CATALOGUE_PERMISSIONS: readonly Permission[] = [
  { code: 'session:read:self', description: 'Lire sa propre session et ses rôles.' },
  { code: 'session:write:self', description: 'Fermer sa session, demander ou vérifier un code OTP.' },
  { code: 'impersonation:act:all', description: 'Voir en tant que (lecture par défaut, action confirmée).' },
  { code: 'impersonation:read:all', description: 'Lire l’historique des sessions « voir en tant que ».' },
  { code: 'pilotage:read:all', description: 'Tableau de bord de tous les dossiers et pilotage financier.', ligne: 'pilotage_financier' },
  { code: 'dossier:read:dossier', description: 'Lire un dossier et sa progression détaillée.', ligne: 'progression' },
  { code: 'dossier:write:dossier', description: 'Créer ou modifier un dossier.' },
  { code: 'procedure:write:dossier', description: 'Ajouter, sauter ou réordonner une étape de procédure.' },
  { code: 'journal:export:dossier', description: 'Exporter le journal horodaté.', ligne: 'journal_exportable' },
  { code: 'task:read:self', description: 'Lire sa file du jour.' },
  { code: 'task:write:dossier', description: 'Créer ou modifier une tâche.' },
  { code: 'decision:read:all', description: 'Lire les décisions en attente.' },
  { code: 'decision:write:all', description: 'Trancher une décision.' },
  { code: 'genealogy:read:dossier', description: 'Lire l’arbre complet de la famille.', ligne: 'arbre_complet' },
  { code: 'genealogy:write:dossier', description: 'Ajouter ou modifier personnes, liens, options.' },
  { code: 'person.contact:read:dossier', description: 'Lire les coordonnées des héritiers.', ligne: 'coordonnees_heritiers' },
  { code: 'asset:read:dossier', description: 'Lire les biens, titres et interrogations.' },
  { code: 'asset:write:dossier', description: 'Ajouter ou modifier un bien.' },
  { code: 'estimate:read:dossier', description: 'Lire l’estimation des parts (équipe seulement).' },
  { code: 'notary:read:dossier', description: 'Lire les interrogations et renonciations.' },
  { code: 'notary:write:dossier', description: 'Mettre à jour une interrogation notariale.' },
  { code: 'deadline:read:all', description: 'Lire les échéances légales.' },
  { code: 'document:read:dossier', description: 'Lire les pièces et leurs aperçus.' },
  { code: 'document:write:dossier', description: 'Déposer une pièce.', ligne: 'deposer_piece' },
  { code: 'document:validate:dossier', description: 'Valider, refuser ou demander une meilleure photo.' },
  { code: 'mandate:read:dossier', description: 'Lire les mandats et devis.' },
  { code: 'mandate:write:dossier', description: 'Envoyer un mandat, un devis ou une procuration à signer.' },
  { code: 'message:read:all', description: 'Lire la boîte unifiée.' },
  { code: 'message:write:dossier', description: 'Rattacher un fil, préparer ou envoyer un message.' },
  {
    code: 'message.isabelle:validate:all',
    description: 'Valider un message préparé depuis le WhatsApp d’Isabelle.',
    ligne: 'whatsapp_isabelle',
  },
  { code: 'followup:read:all', description: 'Lire les relances programmées.' },
  { code: 'followup:write:dossier', description: 'Déclencher, suspendre ou reprendre une relance.', ligne: 'relances' },
  { code: 'template:read:all', description: 'Lire les modèles de messages.' },
  { code: 'template:write:all', description: 'Versionner un modèle de message.' },
  { code: 'billing:read:dossier', description: 'Lire la facturation détaillée d’un dossier.' },
  { code: 'billing:write:dossier', description: 'Modifier règles, échéanciers et factures (confirmation explicite).' },
  { code: 'payment:write:dossier', description: 'Créer des liens de paiement.' },
  { code: 'consent:read:dossier', description: 'Lire les consentements d’une personne.' },
  { code: 'consent:write:dossier', description: 'Enregistrer un consentement recueilli.' },
  { code: 'sensitive:read:dossier', description: 'Lire les étiquettes sensibles (équipe MAAT seulement).' },
  { code: 'sensitive:share:dossier', description: 'Partager une étiquette sensible hors équipe, avec alerte.' },
  { code: 'note:read:dossier', description: 'Lire les notes internes (coulisses).', ligne: 'notes_internes' },
  { code: 'note:write:dossier', description: 'Ajouter une note interne.' },
  { code: 'compte_rendu:read:dossier', description: 'Lire les comptes rendus d’entretien et d’appel d’un dossier (internes au cabinet).' },
  { code: 'compte_rendu:read:all', description: 'Lire les comptes rendus à faire et à relire de tous les dossiers.' },
  { code: 'compte_rendu:write:dossier', description: 'Ajouter, générer et corriger un compte rendu.' },
  { code: 'compte_rendu:validate:dossier', description: 'Valider un compte rendu relu.' },
  { code: 'acte:read:dossier', description: 'Lire les actes notariés du dossier et leur statut.' },
  { code: 'acte:write:dossier', description: 'Enregistrer, modifier ou retirer un acte notarié du dossier.' },
  { code: 'contact:import:dossier', description: 'Importer une liste de contacts (CSV) dans un dossier, après aperçu et confirmation.' },
  { code: 'video:read:dossier', description: 'Lire les vidéos du dossier (réservées à l’équipe).' },
  { code: 'video:write:dossier', description: 'Rattacher ou retirer une vidéo du dossier.' },
  { code: 'video:share:dossier', description: 'Partager une vidéo au portail de la famille, avec l’alerte qui liste les destinataires.' },
  {
    code: 'compensation:read:dossier',
    description: 'Lire les sommes entre héritiers et les pistes de remboursement (lecture, calcul à valider).',
  },
  // RG-24 (RFC-RG24-001) : temps passé dans un dossier, équipe MAAT seulement.
  { code: 'time:read:dossier', description: 'Lire le temps passé dans un dossier (périodes et corrections).' },
  { code: 'time:write:dossier', description: 'Enregistrer le temps passé dans un dossier et le corriger avec un motif.' },
  // L64 (30/10) : démarrage guidé d'un dossier, équipe de bureau seulement.
  { code: 'demarrage:read:dossier', description: 'Lire l’avancement du démarrage guidé d’un dossier.' },
  { code: 'demarrage:write:dossier', description: 'Marquer une étape du démarrage guidé et répondre à sa question.' },
  { code: 'privacy:write:self', description: 'Déposer une demande RGPD pour soi.' },
  { code: 'admin:read:all', description: 'Lire utilisateurs, études et paramètres légaux.' },
  { code: 'admin:write:all', description: 'Gérer utilisateurs, études, clercs et paramètres légaux.' },
  { code: 'beta:read:self', description: 'Lire ses questions BETA et les réglages appris.' },
  { code: 'beta:write:self', description: 'Répondre à une question BETA.' },
  { code: 'portal:read:branche', description: 'Portail : prochaine action, sa branche, son reste dû.' },
  { code: 'portal:write:self', description: 'Portail : déposer une pièce, régler ses préférences.' },
  { code: 'clerc:read:etude', description: 'Espace clerc : dossiers transmis à son étude.' },
  { code: 'notaire:read:dossier', description: 'Espace notaire : tableaux, titres, actes.' },
  { code: 'sync:write:self', description: 'Synchronisation hors ligne de ses mutations.' },
];
